import { RLayer, RSource, useMap } from "maplibre-react-components";
import React, { useMemo } from "react";
import type {
  FillLayerSpecification,
  SymbolLayerSpecification,
} from "maplibre-gl";
import { useSubscriptionStore } from "@/store/useSubscriptionStore";
import { useTariffLayer } from "@/store/mode";
import { makeAgglomerationColorExpression } from "@/utils/helpers";
import { useAgglomerationsDataContext } from "@/store/AgglomerationsProvider";
import { usePriceRangesDataContext } from "@/store/PriceRangesProvider";
import { useMapIcon } from "@/utils/hooks/useIconLoaded";
import { isEdaTariffMode, isKuperTariffMode } from "@/types/base";

const templateStops = [50, 100, 200, 500, 1000, 1500];

const FILL_ID = "agglomerations-fill";
const LABEL_ID = "agglomerations-labels";
const LABEL_SOURCE_ID = "agglomerations-labels-src";

const AgglomerationsLayer = () => {
  const map = useMap();
  const iconLoaded = useMapIcon(map, "/assets/label.svg", "price-pill");

  const tariffLayer = useTariffLayer((s) => s.tariffLayer);
  const isEdaMode = isEdaTariffMode(tariffLayer);
  const isKuperMode = isKuperTariffMode(tariffLayer);
  const { agglomerations } = useAgglomerationsDataContext();
  const { agglomerationsPriceRanges } = usePriceRangesDataContext();
  const { isSubscriptionActive } = useSubscriptionStore();

  const tilesUrl = useMemo(() => {
    return [`${__MARTIN_URL__}/public.agglomerations/{z}/{x}/{y}.pbf`];
  }, []);

  const globalRange = useMemo(() => {
    if (!agglomerationsPriceRanges) return { min: 100, max: 800 };

    const range = agglomerationsPriceRanges.global_ranges[tariffLayer];
    if (range) return range;

    return { min: 100, max: 800 };
  }, [agglomerationsPriceRanges, tariffLayer]);

  const priceByAgglomerationId = useMemo(() => {
    if (!agglomerationsPriceRanges) return {};

    const m: Record<number, number> = {};

    agglomerationsPriceRanges.agglomerations.forEach((a) => {
      const range = a.ranges[tariffLayer];
      if (range && range.max > 0) {
        m[a.id] = range.max - range.min;
      }
    });

    return m;
  }, [agglomerationsPriceRanges, tariffLayer]);

  const labelFeatures = useMemo<
    GeoJSON.FeatureCollection<GeoJSON.Point>
  >(() => {
    return {
      type: "FeatureCollection",
      features: (agglomerations ?? [])
        .map((agglomeration) => {
          const price = priceByAgglomerationId[agglomeration.id] ?? 0;

          if (price <= 0) return null;

          return {
            type: "Feature",
            id: agglomeration.id,
            geometry: {
              type: "Point",
              coordinates: [
                agglomeration.center.lon,
                agglomeration.center.lat,
              ] as [number, number],
            },
            properties: {
              id: agglomeration.id,
              price,
            },
          };
        })
        .filter(Boolean) as GeoJSON.Feature<GeoJSON.Point>[],
    };
  }, [agglomerations, priceByAgglomerationId]);

  const agglomerationsFill = useMemo<FillLayerSpecification>(() => {
    return {
      id: FILL_ID,
      type: "fill",
      source: "agglomerations-src",
      "source-layer": "public.agglomerations",
      minzoom: 0,
      maxzoom: 5,
      paint: {
        "fill-color": makeAgglomerationColorExpression(
          priceByAgglomerationId,
          "id",
          globalRange.min,
          globalRange.max,
          templateStops
        ),
        "fill-opacity": ["interpolate", ["linear"], ["zoom"], 4.5, 1, 5, 0],
      },
    };
  }, [priceByAgglomerationId, globalRange]);

  const agglomerationsLabels = useMemo<SymbolLayerSpecification>(() => {
    return {
      id: LABEL_ID,
      type: "symbol",
      source: LABEL_SOURCE_ID,
      minzoom: 0,
      maxzoom: 4.5,
      layout: {
        "text-field": [
          "concat",
          "+",
          ["to-string", ["get", "price"]],
          "\u20BD",
        ],
        "icon-image": "price-pill",
        "icon-text-fit": "both",
        "icon-text-fit-padding": [4, 10, 4, 10],
        "text-size": [
          "interpolate",
          ["linear"],
          ["get", "price"],
          100,
          12,
          200,
          14,
          400,
          16,
          600,
          18,
        ],
        "text-anchor": "center",
        "text-allow-overlap": false,
        "icon-allow-overlap": false,
        "symbol-sort-key": ["*", -1, ["get", "price"]],
      },
      paint: {
        "text-color": "#F349FF",
        "icon-opacity": 1,
      },
    };
  }, []);

  if (!isSubscriptionActive || !iconLoaded || isEdaMode || isKuperMode)
    return null;

  return (
    <>
      <RSource
        id="agglomerations-src"
        type="vector"
        tiles={tilesUrl}
        minzoom={0}
        maxzoom={14}
      />
      <RSource id={LABEL_SOURCE_ID} type="geojson" data={labelFeatures} />
      <RLayer key={FILL_ID} {...agglomerationsFill} />
      <RLayer key={LABEL_ID} {...agglomerationsLabels} />
    </>
  );
};

export default AgglomerationsLayer;
